import React, { useState, useEffect } from 'react';
import { Product } from '../App';
import { Lang, langNames } from '../i18n';

export default function Home({ onProductClick, onMerchantClick, products, categories, merchants, t, lang, changeLang }: { onProductClick: (p: Product) => void; onMerchantClick: (id: string) => void; products: Product[]; categories: {name: string; icon: string}[]; merchants: any[]; t: (key: string) => string; lang: Lang; changeLang: (lang: Lang) => void }) {
  const BRAND_COLORS = ['#611f69', '#0070ba', '#e85d04', '#2a9d8f', '#264653', '#e76f51', '#3a86ff', '#8338ec'];
  const [seckillTime, setSeckillTime] = useState({ h: 0, m: 0, s: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [announcement, setAnnouncement] = useState<{title: string, content: string} | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  const filteredProducts = searchQuery.trim()
    ? products.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : products;

  const filteredMerchants = searchQuery.trim()
    ? merchants.filter(m => 
        m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : [];

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const end = new Date(now);
      end.setHours(end.getHours() + 1, 0, 0, 0);
      const diff = end.getTime() - now.getTime();
      setSeckillTime({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadAnnouncement = async () => {
      try {
        const API = 'https://api.314pi.dpdns.org';
        const currentLang = localStorage.getItem('lang') || 'zh';
        const titleKey = currentLang === 'en' ? 'announcement_title_en' : 'announcement_title';
        const contentKey = currentLang === 'en' ? 'announcement_content_en' : 'announcement_content';
        
        const [titleRes, contentRes, showRes] = await Promise.all([
          fetch(API + '/settings/' + titleKey).then(r => r.json()),
          fetch(API + '/settings/' + contentKey).then(r => r.json()),
          fetch(API + '/settings/announcement_show').then(r => r.json())
        ]);
        
        if (showRes.value === 'true') {
          setAnnouncement({
            title: titleRes.value || '',
            content: contentRes.value || ''
          });
        }
      } catch (e) {}
    };
    loadAnnouncement();
  }, []);

  return (
    <div>
        {/* Search Bar */}
      <div className="search-bar">
        <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
          <div style={{position:'relative', flex:1}}>
            <input 
              className="search-input" 
              placeholder={"🔍 " + t('search')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsSearching(e.target.value.trim().length > 0);
              }}
              onFocus={() => {
                if (searchQuery.trim()) setIsSearching(true);
              }}
            />
            {isSearching && (
              <span 
                style={{position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', color:'#999', cursor:'pointer', fontSize:'18px'}}
                onClick={() => { setSearchQuery(''); setIsSearching(false); }}
              >✕</span>
            )}
          </div>
          <div style={{position:'relative'}}>
            <span 
              style={{color:'white', cursor:'pointer', fontSize:'12px', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'2px'}}
              onClick={() => setShowLangMenu(!showLangMenu)}
            >🌐 {lang.toUpperCase()}</span>
            {showLangMenu && (
              <div style={{position:'absolute', right:'0', top:'100%', background:'#fff', borderRadius:'10px', boxShadow:'0 4px 16px rgba(0,0,0,0.15)', zIndex:999, minWidth:'140px', overflow:'hidden', marginTop:'4px'}}>
                {(Object.keys(langNames) as Lang[]).map((l) => (
                  <div
                    key={l}
                    onClick={() => { changeLang(l); setShowLangMenu(false); }}
                    style={{padding:'10px 16px', fontSize:'14px', cursor:'pointer', background: lang === l ? '#f0eeff' : 'transparent', color: lang === l ? '#6C5CE7' : '#333', fontWeight: lang === l ? 600 : 400}}
                  >
                    {langNames[l]}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Banner */}
      <div className="banner">
        <div>
          <h2>{t('home.banner.title')}</h2>
          <p>{t('home.banner.desc')}</p>
        </div>
      </div>

      {/* 平台公告 */}
      {announcement && (
        <div style={{
          background: '#fff3e0',
          border: '1px solid #e65100',
          borderRadius: '12px',
          padding: '16px',
          margin: '16px 24px',
          color: '#e65100'
        }}>
          <h3 style={{margin: '0 0 8px', fontSize: '16px'}}>📢 {announcement.title}</h3>
          <div dangerouslySetInnerHTML={{__html: announcement.content}} style={{fontSize: '14px', lineHeight: 1.6}} />
        </div>
      )}

      {/* 热门店铺 */}
      {merchants.length > 0 && (
        <div style={{padding:'16px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
            <span style={{fontSize:'16px', fontWeight:'bold', color:'#333'}}>🏪 {t('home.hotStores') || 'Hot Stores'}</span>
            <span style={{fontSize:'13px', color:'#611f69', cursor:'pointer'}} onClick={() => {}}>更多 ›</span>
          </div>
          <div style={{display:'flex', gap:'10px', overflowX:'auto', paddingBottom:'8px'}}>
            {merchants.map((m: any) => (
              <div key={m.id} onClick={() => onMerchantClick(String(m.id))} style={{
                flex:'0 0 auto', width:'140px', background:'#fff', borderRadius:'12px', 
                padding:'12px', textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.08)',
                cursor:'pointer'
              }}>
                <div style={{
                  width:'60px', height:'60px', borderRadius:'50%', background:'#f0f0f0',
                  margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'28px', overflow:'hidden'
                }}>
                  {m.logo?.trim() ? (
                    <img 
                      src={m.logo} 
                      style={{width:'100%', height:'100%', objectFit:'cover'}} 
                      onError={(e: any) => e.target.style.display='none'} 
                    />
                  ) : (
                    <span style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '22px', 
                      fontWeight: 'bold', 
                      color: '#fff',
                      background: BRAND_COLORS[m.id % BRAND_COLORS.length]
                    }}>
                      {m.name?.charAt(0).toUpperCase() || '店'}
                    </span>
                  )}
                </div>
                <div style={{fontSize:'13px', fontWeight:'500', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{m.name}</div>
                <div style={{fontSize:'11px', color:'#999', marginTop:'4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{m.description || ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="categories" style={{
        display: 'flex',
        overflowX: 'auto',
        gap: '12px',
        padding: '12px 16px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        <style>{`.categories::-webkit-scrollbar { display: none; }`}</style>
        
        {categories.map((cat: any) => (
          <div 
            className="category-item" 
            key={cat.id || cat.slug || cat.name}
            onClick={() => {
              // 点击分类：跳转到分类筛选或搜索页
              if (cat.name) {
                // 方案A：本地筛选（如果首页有商品列表）
                setSelectedCategory(cat.name);
                // 方案B：跳转URL（如果需要单独分类页）
                // window.location.href = '/class?category=' + encodeURIComponent(cat.name);
              }
            }}
            style={{
              flexShrink: 0,
              width: '72px',
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: selectedCategory === cat.name ? '#f3e5f5' : '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              margin: '0 auto 8px',
              border: selectedCategory === cat.name ? '2px solid #611f69' : '2px solid transparent'
            }}>
              {cat.icon}
            </div>
            <div style={{
              fontSize: '12px',
              color: selectedCategory === cat.name ? '#611f69' : '#333',
              fontWeight: selectedCategory === cat.name ? 600 : 400,
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {/* 关键修复：slug 不存在时直接显示原名，不拼接 cat.undefined */}
              {cat.slug && cat.slug !== 'undefined' 
                ? (t('cat.' + cat.slug) || cat.name) 
                : cat.name
              }
            </div>
          </div>
        ))}
      </div>

      {/* 显示当前筛选状态 */}
      {selectedCategory && (
        <div style={{padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px'}}>
          <span style={{fontSize: '14px', color: '#666'}}>
            {t('home.filterBy') || '分类'}: {selectedCategory}
          </span>
          <button 
            onClick={() => setSelectedCategory(null)}
            style={{
              padding: '4px 12px',
              fontSize: '12px',
              color: '#611f69',
              background: '#f3e5f5',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
          >
            {t('home.clearFilter') || '清除'}
          </button>
        </div>
      )}

      {/* Seckill */}
      <div className="seckill">
        <div className="seckill-header">
          <span className="seckill-title">{t('home.seckill')}</span>
          <span className="seckill-timer">
            {String(seckillTime.h).padStart(2,'0')}:{String(seckillTime.m).padStart(2,'0')}:{String(seckillTime.s).padStart(2,'0')}
          </span>
          <span className="seckill-more">{t('home.more')}</span>
        </div>
        <div className="seckill-products">
          {(isSearching ? [] : products).slice(0, 4).map(p => (

            <div className="seckill-product" key={p.id} onClick={() => onProductClick(p)}>
              <img src={p.image} alt={p.name} onError={(e: any) => e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect fill="%23f0f0f0" width="80" height="80"/><text x="40" y="45" text-anchor="middle" fill="%23ccc" font-size="12">No Image</text></svg>'} />
              <div className="seckill-price">π{p.piPrice}</div>
              <div className="seckill-original">¥{p.originalPrice}</div>
            </div>
          ))}
        </div>
      </div>

           {/* Search Results or Product Grid */}
      {isSearching ? (
        <div>
          {/* 搜索结果 - 店铺 */}
          {filteredMerchants.length > 0 && (
            <div className="product-section">
              <div className="product-section-title">🏪 {t('store.title') || 'Stores'} ({filteredMerchants.length})</div>
              <div style={{display:'flex', gap:'10px', overflowX:'auto', padding:'0 16px 16px'}}>
                {filteredMerchants.map((m: any) => (
                  <div key={m.id} onClick={() => onMerchantClick(String(m.id))} style={{
                    flex:'0 0 auto', width:'140px', background:'#fff', borderRadius:'12px',
                    padding:'12px', textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.08)',
                    cursor:'pointer'
                  }}>
                    <div style={{
                      width:'50px', height:'50px', borderRadius:'50%', background:'#f0f0f0',
                      margin:'0 auto 8px', display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'24px', overflow:'hidden'
                    }}>
                      {m.logo?.trim() ? (
                        <img src={m.logo} style={{width:'100%', height:'100%', objectFit:'cover'}} onError={(e: any) => e.target.style.display='none'} />
                      ) : (
                        <span style={{
                          width: '100%', height: '100%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontSize: '22px', fontWeight: 'bold', color: '#fff',
                          background: BRAND_COLORS[m.id % BRAND_COLORS.length]
                        }}>
                          {m.name?.charAt(0).toUpperCase() || '店'}
                        </span>
                      )}

                    </div>
                    <div style={{fontSize:'13px', fontWeight:'500', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{m.name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 搜索结果 - 商品 */}
          <div className="product-section">
            <div className="product-section-title">📦 {t('tab.products').replace(/📦 /, '') || 'Products'} ({filteredProducts.length})</div>
            {filteredProducts.length === 0 ? (
              <div style={{textAlign:'center', padding:'40px 20px', color:'#999'}}>
                <div style={{fontSize:48}}>🔍</div>
                <p>{filteredMerchants.length === 0 && filteredProducts.length === 0 ? `${t('noResults')} "${searchQuery}"` : ''}</p>
              </div>
            ) : (
              <div className="product-grid">
                {filteredProducts.map(p => (
                  <div className="product-card" key={p.id} onClick={() => onProductClick(p)}>
                    <img src={p.image} alt={p.name} onError={(e: any) => e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 180"><rect fill="%23f0f0f0" width="200" height="180"/><text x="100" y="95" text-anchor="middle" fill="%23ccc" font-size="14">No Image</text></svg>'} />
                    <div className="product-card-info">
                      <div className="product-card-name">{p.name}</div>
                      <div className="product-card-prices">
                        <span className="pi-price">{p.piPrice}</span>
                        <span className="original-price">¥{p.originalPrice}</span>
                      </div>
                      {p.coupon && <div className="product-card-coupon">{p.coupon}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="product-section">
          <div className="product-section-title">{t('home.recommend')}</div>
          <div className="product-grid">
            {products.map(p => (
              <div className="product-card" key={p.id} onClick={() => onProductClick(p)}>
                <img src={p.image} alt={p.name} onError={(e: any) => e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 180"><rect fill="%23f0f0f0" width="200" height="180"/><text x="100" y="95" text-anchor="middle" fill="%23ccc" font-size="14">No Image</text></svg>'} />
                <div className="product-card-info">
                  <div className="product-card-name">{p.name}</div>
                  <div className="product-card-prices">
                    <span className="pi-price">{p.piPrice}</span>
                    <span className="original-price">¥{p.originalPrice}</span>
                  </div>
                  {p.coupon && <div className="product-card-coupon">{p.coupon}</div>}
                  {p.sales && <div className="product-card-sales">{p.sales > 1000 ? (p.sales/1000).toFixed(1)+'k' : p.sales} {t('detail.sold')}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
